import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import axios from 'axios';
import { 
  Plus, 
  PencilSimple, 
  Trash,
  ForkKnife,
  ListBullets,
  MagnifyingGlass,
  CaretDown,
  Check,
  X,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

export const MenuPage = () => {
  const { currentOutlet, categories, menuItems, modifierGroups, fetchMenuData } = useApp();
  const [selectedTab, setSelectedTab] = useState('items');
  const [searchQuery, setSearchQuery] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    category_id: '',
    dine_in_price: '',
    takeaway_price: '',
    delivery_price: '',
    kitchen_station: 'main',
    is_available: true,
    modifier_group_ids: [],
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    sort_order: 0,
  });

  const filteredItems = menuItems.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateItem = async () => {
    if (!currentOutlet) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/menu-items`, {
        ...itemForm,
        outlet_id: currentOutlet.id,
        dine_in_price: parseFloat(itemForm.dine_in_price),
        takeaway_price: itemForm.takeaway_price ? parseFloat(itemForm.takeaway_price) : null,
        delivery_price: itemForm.delivery_price ? parseFloat(itemForm.delivery_price) : null,
      }, { withCredentials: true });
      
      await fetchMenuData();
      setShowItemModal(false);
      resetItemForm();
    } catch (e) {
      console.error('Error creating item:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/menu-items/${editingItem.id}`, {
        ...itemForm,
        outlet_id: currentOutlet.id,
        dine_in_price: parseFloat(itemForm.dine_in_price),
        takeaway_price: itemForm.takeaway_price ? parseFloat(itemForm.takeaway_price) : null,
        delivery_price: itemForm.delivery_price ? parseFloat(itemForm.delivery_price) : null,
      }, { withCredentials: true });
      
      await fetchMenuData();
      setShowItemModal(false);
      resetItemForm();
    } catch (e) {
      console.error('Error updating item:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/menu-items/${itemId}`, { withCredentials: true });
      await fetchMenuData();
    } catch (e) {
      console.error('Error deleting item:', e);
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      await axios.patch(
        `${API_URL}/api/menu-items/${item.id}/availability?is_available=${!item.is_available}`,
        {},
        { withCredentials: true }
      );
      await fetchMenuData();
    } catch (e) {
      console.error('Error toggling availability:', e);
    }
  };

  const handleCreateCategory = async () => {
    if (!currentOutlet) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/categories`, {
        ...categoryForm,
        outlet_id: currentOutlet.id,
      }, { withCredentials: true });
      
      await fetchMenuData();
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '', sort_order: 0 });
    } catch (e) {
      console.error('Error creating category:', e);
    } finally {
      setLoading(false);
    }
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      description: '',
      category_id: '',
      dine_in_price: '',
      takeaway_price: '',
      delivery_price: '',
      kitchen_station: 'main',
      is_available: true,
      modifier_group_ids: [],
    });
    setEditingItem(null);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      description: item.description || '',
      category_id: item.category_id,
      dine_in_price: item.dine_in_price.toString(),
      takeaway_price: item.takeaway_price?.toString() || '',
      delivery_price: item.delivery_price?.toString() || '',
      kitchen_station: item.kitchen_station || 'main',
      is_available: item.is_available,
      modifier_group_ids: item.modifier_group_ids || [],
    });
    setShowItemModal(true);
  };

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || 'Unknown';
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to manage menu</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="menu-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Menu Management</h1>
          <p className="text-muted-foreground mt-1">{currentOutlet?.name}</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowCategoryModal(true)}
            data-testid="add-category-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Category
          </Button>
          <Button
            onClick={() => { resetItemForm(); setShowItemModal(true); }}
            className="bg-primary hover:bg-primary-hover text-white"
            data-testid="add-item-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="items" data-testid="tab-items">
            <ForkKnife size={18} className="mr-2" />
            Menu Items ({menuItems.length})
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            <ListBullets size={18} className="mr-2" />
            Categories ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="modifiers" data-testid="tab-modifiers">
            Modifiers ({modifierGroups.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-6">
          {/* Search */}
          <div className="relative mb-4 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Item</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Category</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Dine-In</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Takeaway</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Delivery</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Station</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Available</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-surface transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-surface flex items-center justify-center">
                            <ForkKnife size={20} className="text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{getCategoryName(item.category_id)}</td>
                    <td className="px-4 py-3 font-medium">${item.dine_in_price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">{item.takeaway_price ? `$${item.takeaway_price.toFixed(2)}` : '-'}</td>
                    <td className="px-4 py-3 text-sm">{item.delivery_price ? `$${item.delivery_price.toFixed(2)}` : '-'}</td>
                    <td className="px-4 py-3 text-sm capitalize">{item.kitchen_station}</td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={item.is_available}
                        onCheckedChange={() => handleToggleAvailability(item)}
                        data-testid={`toggle-${item.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditItem(item)}
                          data-testid={`edit-item-${item.id}`}
                        >
                          <PencilSimple size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-accent-red"
                          data-testid={`delete-item-${item.id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div key={category.id} className="bg-white rounded-lg border border-border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg">{category.name}</h3>
                  <span className="text-xs text-muted-foreground">Order: {category.sort_order}</span>
                </div>
                {category.description && (
                  <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
                )}
                <p className="text-sm mt-2">
                  {menuItems.filter(i => i.category_id === category.id).length} items
                </p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="modifiers" className="mt-6">
          <div className="space-y-4">
            {modifierGroups.map((group) => (
              <div key={group.id} className="bg-white rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-heading font-bold text-lg">{group.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {group.selection_type === 'single' ? 'Single select' : 'Multi select'}
                      {group.is_required && ' • Required'}
                      {group.max_selections > 1 && ` • Max ${group.max_selections}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.options?.map((option) => (
                    <span
                      key={option.id}
                      className="px-3 py-1 bg-surface rounded-full text-sm"
                    >
                      {option.name}
                      {option.price > 0 && ` (+$${option.price.toFixed(2)})`}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="Item name"
                data-testid="item-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder="Description"
              />
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={itemForm.category_id}
                onValueChange={(value) => setItemForm({ ...itemForm, category_id: value })}
              >
                <SelectTrigger data-testid="item-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Dine-In Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.dine_in_price}
                  onChange={(e) => setItemForm({ ...itemForm, dine_in_price: e.target.value })}
                  placeholder="0.00"
                  data-testid="item-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Takeaway Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.takeaway_price}
                  onChange={(e) => setItemForm({ ...itemForm, takeaway_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Delivery Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.delivery_price}
                  onChange={(e) => setItemForm({ ...itemForm, delivery_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Kitchen Station</Label>
              <Select
                value={itemForm.kitchen_station}
                onValueChange={(value) => setItemForm({ ...itemForm, kitchen_station: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main Kitchen</SelectItem>
                  <SelectItem value="pizza">Pizza Station</SelectItem>
                  <SelectItem value="grill">Grill Station</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="dessert">Dessert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Modifier Groups</Label>
              <div className="flex flex-wrap gap-2">
                {modifierGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => {
                      const ids = itemForm.modifier_group_ids;
                      if (ids.includes(group.id)) {
                        setItemForm({ ...itemForm, modifier_group_ids: ids.filter(id => id !== group.id) });
                      } else {
                        setItemForm({ ...itemForm, modifier_group_ids: [...ids, group.id] });
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                      itemForm.modifier_group_ids.includes(group.id)
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white border-border hover:border-primary'
                    }`}
                  >
                    {group.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={itemForm.is_available}
                onCheckedChange={(checked) => setItemForm({ ...itemForm, is_available: checked })}
              />
              <Label>Available for ordering</Label>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => { setShowItemModal(false); resetItemForm(); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={editingItem ? handleUpdateItem : handleCreateItem}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              disabled={loading || !itemForm.name || !itemForm.category_id || !itemForm.dine_in_price}
              data-testid="save-item-btn"
            >
              {loading ? 'Saving...' : editingItem ? 'Update Item' : 'Create Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Category</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Category name"
                data-testid="category-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Description"
              />
            </div>
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={categoryForm.sort_order}
                onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowCategoryModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCategory}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              disabled={loading || !categoryForm.name}
              data-testid="save-category-btn"
            >
              {loading ? 'Creating...' : 'Create Category'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuPage;
